/**
 * The panel: one bordered surface, in the two tones the elevation model allows.
 *
 * The brand carries depth on background tone and a hairline border, never a
 * shadow, so there are two levels and no more: a panel on the canvas, an inset
 * panel inside one. Anything deeper is a hierarchy problem, not a styling one.
 *
 * Heading and surface stay separate concerns, unlike the `SectionCard` this
 * replaces, so a panel can carry a heading, several, or none.
 *
 * @module components/ui/Panel
 */

import React, { type ReactNode, useId, useState } from "react";

import { Icon } from "./Icon";
import { Eyebrow, Heading } from "./Text";

interface PanelBaseProps {
  /** For a panel a link or a notification targets by anchor. */
  id?: string;
  /** Phosphor icon beside the title. */
  icon?: string;
  /** Sits opposite the title: one action, or a small group. */
  actions?: ReactNode;
  /** Explanatory line under the title. */
  description?: ReactNode;
  /** `inset` is the lighter surface for a panel nested inside a panel. */
  tone?: "default" | "inset";
  /**
   * A background class that replaces the tone's, for a lane that carries a
   * meaning of its own. Appended to `className` it would fight the tone for
   * the same property.
   */
  tint?: string;
  /** Squares the top-left corner, where a `lift` tab strip above joins the panel. */
  attached?: boolean;
  /** Drops the inner padding, for a panel whose body is a full-bleed table. */
  flush?: boolean;
  /**
   * `compact` is the tighter padding for a screen of many small panels: p-4
   * where `default` is p-6. A row of them sizes to its content, so lay it out
   * with `items-start`.
   */
  density?: "default" | "compact";
  /** Heading level, which also sets the heading's size through `Heading`. */
  headingLevel?: 2 | 3 | 4;
  /**
   * Names the region when its heading is too short to tell it apart, as two
   * panels of the same shape side by side are. Replaces the heading as the
   * name rather than joining it, so start it with the heading's own words.
   */
  "aria-label"?: string;
  className?: string;
  children?: ReactNode;
  "data-testid"?: string;
}

/**
 * `collapsible` carries its `title` in the type rather than in a doc comment:
 * the heading is the control, so a collapsible panel without one renders a
 * surface nobody can fold, which typechecked and shipped three times.
 */
export type PanelProps = PanelBaseProps &
  (
    | {
        collapsible: true;
        title: ReactNode;
        /** Whether it starts open. Read once, so a later change cannot reopen it. */
        defaultOpen?: boolean;
      }
    | {
        collapsible?: false;
        /** Optional heading row. Omit for a plain surface. */
        title?: ReactNode;
        /** A small uppercase label above the title, such as "Stage 2". */
        eyebrow?: ReactNode;
        defaultOpen?: never;
      }
  );

const PADDING: Record<"default" | "compact", string> = {
  default: "p-6 max-sm:p-4",
  compact: "p-4",
};

/** The header of a flush panel carries the padding the body gave up. */
const FLUSH_HEADER_PADDING: Record<"default" | "compact", string> = {
  default: "p-6 pb-0 max-sm:p-4 max-sm:pb-0",
  compact: "p-4 pb-0",
};

const FLUSH_INSET: Record<"default" | "compact", string> = {
  default: "px-6 max-sm:px-4",
  compact: "px-4",
};

const TONE_CLASS: Record<"default" | "inset", string> = {
  default: "bg-base-200",
  inset: "bg-base-100",
};

/**
 * The widened view the implementation destructures. The union above is what
 * callers see; a component signature typed on it cannot also carry per-prop
 * defaults, which `react/require-default-props` requires.
 */
type PanelInternalProps = PanelBaseProps & {
  collapsible?: boolean;
  title?: ReactNode;
  eyebrow?: ReactNode;
  defaultOpen?: boolean;
};

function PanelImpl({
  id = undefined,
  title = undefined,
  eyebrow = undefined,
  icon = undefined,
  actions = undefined,
  description = undefined,
  tone = "default",
  tint = undefined,
  attached = false,
  flush = false,
  density = "default",
  headingLevel = 2,
  collapsible = false,
  defaultOpen = true,
  className = "",
  children = undefined,
  "aria-label": ariaLabel = undefined,
  "data-testid": testId,
}: PanelInternalProps): React.ReactElement {
  // Read once. React writes `open` to the DOM on every change of the prop, so
  // a value derived from loaded data would reopen a panel the reader folded.
  const [startOpen] = useState(defaultOpen);
  // A `section` is only a region a reader can navigate to once it has a
  // name, and the panel's own heading is that name.
  const titleId = useId();
  // `aria-label` wins over `aria-labelledby` anyway, so setting both would
  // leave the heading's id in the markup claiming a name nothing reads.
  const labelledBy = ariaLabel || !title ? undefined : titleId;
  const classes = [
    "rounded-box border border-base-300",
    tint ?? TONE_CLASS[tone],
    attached ? "rounded-tl-none" : "",
    flush ? "" : PADDING[density],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const hasHeader = Boolean(title || actions || description || eyebrow);
  const headerPadding = flush ? FLUSH_HEADER_PADDING[density] : "";

  const heading = title && (
    <Heading
      id={titleId}
      level={headingLevel}
      className={`flex items-center gap-2${eyebrow ? "" : " min-h-9"}`}
    >
      {icon && <Icon name={icon} aria-hidden className="text-primary-strong" />}
      {title}
    </Heading>
  );
  const body = children != null && (
    <div className={hasHeader && !flush ? "mt-4" : ""}>{children}</div>
  );

  if (collapsible && title) {
    return (
      <section
        id={id}
        className={classes}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        data-testid={testId}
      >
        <details className="group" open={startOpen}>
          {/* The actions and the description both stay out of the summary. An
              action inside it is a control inside a control; a description
              inside it joins the toggle's accessible name, so a panel loading
              its data announced itself as "Portfolio Loading the portfolio…"
              and then renamed itself when the fetch landed.
              `list-none` hides the marker in Blink and Gecko only; WebKit
              paints its own, which daisyUI's collapse.css suppresses the
              same way. */}
          <summary
            className={`flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden ${headerPadding}`.trim()}
          >
            <div className="min-w-0">{heading}</div>
            <Icon
              name="caret-down"
              aria-hidden
              className="shrink-0 text-muted transition-transform group-open:rotate-180"
            />
          </summary>
          {description && (
            <p className={`mt-1 text-sm text-muted ${flush ? FLUSH_INSET[density] : ""}`.trim()}>
              {description}
            </p>
          )}
          {actions && (
            <div
              className={`flex flex-wrap items-center gap-2 ${flush ? FLUSH_INSET[density] : ""} mt-4`.trim()}
            >
              {actions}
            </div>
          )}
          {body}
        </details>
      </section>
    );
  }

  return (
    <section
      id={id}
      className={classes}
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      data-testid={testId}
    >
      {hasHeader && (
        <div className={`flex flex-wrap items-start justify-between gap-4 ${headerPadding}`.trim()}>
          <div className="min-w-0">
            {eyebrow && <Eyebrow as="div">{eyebrow}</Eyebrow>}
            {heading}
            {description && <p className="mt-1 text-sm text-muted">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {body}
    </section>
  );
}

/**
 * @example
 * ```tsx
 * <Panel title="Review rounds" icon="clipboard-text" actions={<Button…/>}>
 *   …
 * </Panel>
 * ```
 */
export const Panel = PanelImpl as (props: PanelProps) => React.ReactElement;
